import { jwtDecode } from "jwt-decode";

export const isEDMode =
  typeof window !== "undefined"
    ? window.location.href.indexOf("ed=1") > 0
    : false;

export const getUrlParameter = (param) => {
  if (typeof window === "undefined" || !window.location.search || param === undefined) {
    return "";
  }
  param = param.replace(/[\\[]/, "\\[").replace(/[\]]/, "\\]");
  const regex = new RegExp("[\\?&]" + param + "=([^&#]*)");
  const results = regex.exec(window.location.search);

  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
};


export const mobileMedia = "(min-width: 0px) and (max-width: 1200px)";
export const tabletMedia = "(min-width: 768px) and (max-width: 1200px)";

export const isMobile = (media = false) => {
  if (typeof window !== "undefined") {
    return window.matchMedia(media || mobileMedia).matches;
  }

  return true;
};

export const isTablet = () => {
  if (typeof window !== "undefined") {
    return window.matchMedia(tabletMedia).matches;
  }

  return true;
};

export const getWinSize = () => {
  if (typeof window !== "undefined") {
    const w = window;
    const d = document;
    const e = d.documentElement;
    const g = d.getElementsByTagName("body")[0];
    const x = w.innerWidth || e.clientWidth || g.clientWidth;
    const y = w.innerHeight || e.clientHeight || g.clientHeight;

    return { width: x, height: y };
  }

  return { width: 0, height: 0 };
};

// Phone number utilities
export const sanitizePhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // First sanitize to get only digits
  const sanitized = sanitizePhoneNumber(phone);
  
  // If we have 10 digits, format as XXX-XXX-XXXX
  if (sanitized.length === 10) {
    return `${sanitized.slice(0, 3)}-${sanitized.slice(3, 6)}-${sanitized.slice(6)}`;
  }
  
  // If we have less than 10 digits, return as-is (partial input)
  return sanitized;
};

export const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  const sanitized = sanitizePhoneNumber(phone);
  return sanitized.length === 10;
};

// App log merging utilities
export const mergeSimilarLogEntries = (logEntries) => {
  if (!logEntries || logEntries.length === 0) return logEntries;
  
  const merged = [];
  const processed = new Set();
  
  logEntries.forEach((entry, index) => {
    if (processed.has(index)) return;
    
    const similarEntries = [entry];
    const entryTime = new Date(entry.created);
    
    // Find similar entries within the same minute
    for (let i = index + 1; i < logEntries.length; i++) {
      if (processed.has(i)) continue;
      
      const otherEntry = logEntries[i];
      const otherTime = new Date(otherEntry.created);
      
      // Check if entries are similar and within same minute
      // Only merge if code is "Updated ZIP"
      if (
        entry.code === "Updated ZIP" &&
        entry.code === otherEntry.code &&
        entry.created_by === otherEntry.created_by &&
        entry.data?.territory_id === otherEntry.data?.territory_id &&
        Math.abs(entryTime - otherTime) <= 60000 // Within 1 minute
      ) {
        similarEntries.push(otherEntry);
        processed.add(i);
      }
    }
    
    if (similarEntries.length > 1) {
      // Merge the entries
      const mergedEntry = {
        ...entry,
        data: {
          ...entry.data,
          zipcode: similarEntries.map(e => e.data?.zipcode).filter(Boolean).join(', ')
        },
        created: similarEntries[0].created, // Use the first timestamp
        uid: similarEntries.map(e => e.uid).join(',') // Combine UIDs
      };
      merged.push(mergedEntry);
    } else {
      merged.push(entry);
    }
    
    processed.add(index);
  });
  
  return merged;
};

export const debounce = (func, wait, immediate) => {
  let timeout = null;

  return function () {
    const context = this;
    const args = arguments;

    const later = () => {
      timeout = null;

      if (!immediate) {
        func.apply(context, args);
      }
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
};

export const focusLoop = (e, selector) => {
  const tabPressed = e.key === "Tab" || e.keyCode === 9;

  if (!tabPressed) return;

  const elems = document.querySelectorAll(selector);

  if (elems?.length) {
    const firstElem = elems[0];
    const lastElem = elems[elems.length - 1];

    if (!Array.from(elems).includes(document.activeElement)) {
      firstElem.focus();
    }

    if (e.shiftKey) {
      if (document.activeElement === firstElem) {
        lastElem.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElem) {
        firstElem.focus();
        e.preventDefault();
      }
    }
  }
};

export const getCookieByName = (cookieName) => {
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(cookieName + "="));
  if (cookieValue === undefined) {
    return null;
  }
  return cookieValue.split("=")[1];
};

export const scrollToSection = (selector) => {
  if (typeof document !== "undefined") {
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }
};

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

/**
 * @typedef {Object} APIResponse
 * @property {boolean} success - Indicates if the request was successful
 * @property {Object} request - Request details
 * @property {string} request.url - The URL that was called
 * @property {Object} request.headers - Headers used in the request
 * @property {string} request.method - HTTP method used
 * @property {Object} [request.payload] - Request payload/data if any
 * @property {Object} [response] - Response details (only present if no error)
 * @property {number} response.status - HTTP status code
 * @property {any} response.body - Response body
 * @property {string} [err_msg] - Error message (only present if error occurred)
 */

/**
 * Creates an API request handler with token authentication
 * @returns {{
 *   get: (url: string, authToken?: string, data?: Object, header?: Object) => Promise<APIResponse>,
 *   post: (url: string, authToken?: string, data?: Object, header?: Object) => Promise<APIResponse>
 * }}
 */
export const fetchAPIwToken = () => {
  /**
   * Generates headers for the API request
   * @param {string} [authToken] - Authentication token
   * @param {Object} [header] - Additional headers
   * @param {string} [method='GET'] - HTTP method
   * @returns {Object} Headers object
   */
  const getHeaders = (authToken, header, method = 'GET') => {
    const headers = header || {};

    // Only add Content-Type for POST requests
    if (method.toUpperCase() === 'POST') {
      headers["Content-Type"] = "application/json";
    }

    if (authToken) {
      headers["x-api-key"] = authToken;
    }

    return headers;
  };

  const request = {
    /**
     * Performs a GET request
     * @param {string} url - The URL to fetch from
     * @param {string} [authToken] - Authentication token
     * @param {Object} [data] - Query parameters
     * @param {Object} [header] - Additional headers
     * @returns {Promise<APIResponse>} Response object
     */
    get: async (url, authToken, data, header) => {
      const headers = getHeaders(authToken, header, 'GET');
      const queryParams = data ? new URLSearchParams(data).toString() : "";
      const fetchUrl = queryParams ? `${url}?${queryParams}` : url;

      try {
        const response = await fetch(fetchUrl, {
          headers: Object.keys(headers).length ? headers : undefined,
        });

        const json_resp = await response.json();

        return {
          success: response.ok,
          request: { url, headers, method: "get", payload: data },
          response: {
            status: response.status,
            body: json_resp,
          },
        };
      } catch (error) {
        const err_msg = error.stack;
        return {
          success: false,
          request: { url, headers, method: "get", payload: data },
          err_msg,
        };
      }
    },
    /**
     * Performs a POST request
     * @param {string} url - The URL to post to
     * @param {string} [authToken] - Authentication token
     * @param {Object} [data] - Request body
     * @param {Object} [header] - Additional headers
     * @returns {Promise<APIResponse>} Response object
     */
    post: async (url, authToken, data, header) => {
      const headers = getHeaders(authToken, header, 'POST');

      try {
        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        });

        const json_resp = await response.json();
        return {
          success: response.ok,
          request: { url, headers, method: "post", payload: data },
          response: {
            status: response.status,
            body: json_resp,
          },
        };
      } catch (error) {
        const err_msg = error.stack;
        return {
          success: false,
          request: { url, headers, method: "post", payload: data },
          err_msg,
        };
      }
    },
  };

  return request;
};

export const setSessionStorage = (key, value) => {
  if (typeof window !== "undefined") {
    removeSessionStorage(key);
    sessionStorage.setItem(key, value);
  }
};
export const getSessionStorage = (key) => {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem(key);
  }
  return null;
};
export const removeSessionStorage = (key) => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(key);
  }
};
export const isTokenExpired = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken && decodedToken.expiry < Date.now() / 1000;
  } catch (error) {
    return true;
  }
};

/**
 * Handles token refresh when approaching expiration
 * @param {string} token - Current JWT token
 * @returns {Promise<string|null>} New token if refresh successful, null otherwise
 */
export const handleRefreshToken = async (token) => {
  try {
    const decodedToken = jwtDecode(token); // Decoding the JWT token
    const expirationTimeInSeconds =
       Math.floor((new Date(decodedToken.expiry).getTime() - Date.now()) / 1000); // Get remaining seconds until expiration
  
    // check if token will be expired in 5 minutes (300 seconds)
    console.log("expirationTimeInSeconds", expirationTimeInSeconds)
    if (expirationTimeInSeconds <= 300) {
      // Token will expire in 5 minutes or less
      console.log("Token will expire in 5 minutes or less");

      try {
        const response = await fetchAPIwToken().get(
          `${process.env.GATSBY_API_BASE_URL}/portal/login/refresh`,
          token,
        );
        console.log("response", response)
        if (response?.response?.body?.data) {
          return response.response.body.data;
        }
      } catch (error) {
        console.error(error);
      }
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

export const downloadFile = async (type, apiUrl, authToken) => {
  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "x-api-key": authToken,
      },
    });
    const decoded = jwtDecode(authToken);
    const timestamp = new Date().getTime();

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = type === "product_list" ? "product_list_2024.csv" : `${type}_${decoded.sub}_${timestamp}.csv`;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error(error);
  }
};

export const getTestToken = () => {
  // Create a mock payload with common JWT claims
  const payload = {
    sub: "test-user",
    name: "Test User",
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + (60 * 60), // 1 hour from now
    role: "admin",
    admin: true,
  };

  // Base64Url encoding function
  const base64UrlEncode = (str) => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  // Create proper JWT segments
  const header = base64UrlEncode(JSON.stringify({ alg: "none", typ: "JWT" }));
  const content = base64UrlEncode(JSON.stringify(payload));
  const signature = ""; // Empty signature for "none" algorithm

  return `${header}.${content}.${signature}`;
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    timeZone: 'EST',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(',', '') + ' EST';
};

export const formatDateLong = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'EST',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true
  });
};

export const fetchTerritoryList = async ( token, apiBaseUrl ) => {
  const cachedTerritoryList = sessionStorage.getItem("territoryList");
  if (cachedTerritoryList) {
    return JSON.parse(cachedTerritoryList);
  }
  try {
    const result = await fetchAPIwToken().get(`${apiBaseUrl}/territory/list`, token);
    if (result?.success && result.response.status >= 200 && result.response.status < 300 && result.response.body.data ) {
      const data = result.response.body.data.map(item => {
        return {
          ...item,
          isTemp: !item.full_name,
        }
      });
      sessionStorage.setItem("territoryList", JSON.stringify(data));
      return data;
    } else {
      throw new Error("something went wrong");
    }
  } catch (error) {
    console.error(error);
  } 
}